import type {
  RawData,
  TestDefinition,
  TestResult,
  ValidationResult,
} from '../types.ts';
import {
  errorText,
  formatHash,
  formatError,
  isRecord,
  result,
  validation,
  validationError,
} from './shared.ts';

interface NavigatorWithGpu {
  readonly gpu?: {
    requestAdapter(): Promise<{
      readonly info?: Record<string, unknown>;
      readonly features: Set<string>;
      readonly limits: Record<string, number>;
      requestDevice(): Promise<{
        readonly lost: Promise<Record<string, unknown>>;
        destroy(): void;
      }>;
    } | null>;
  };
}

function serializeLimits(
  limits: Record<string, number>,
): Record<string, number> {
  const prototype = Object.getPrototypeOf(limits) as object | null;
  const names = new Set(Object.getOwnPropertyNames(limits));
  if (prototype) {
    for (const name of Object.getOwnPropertyNames(prototype)) {
      names.add(name);
    }
  }
  const serialized: Record<string, number> = {};
  for (const name of [...names].sort()) {
    const value = limits[name];
    if (typeof value == 'number') {
      serialized[name] = value;
    }
  }
  return serialized;
}

async function adapterInfo(): Promise<{
  adapter: Awaited<
    ReturnType<NonNullable<NavigatorWithGpu['gpu']>['requestAdapter']>
  >;
}> {
  const gpu = (navigator as unknown as NavigatorWithGpu).gpu;
  if (!gpu) {
    return { adapter: null };
  }
  const adapter = await gpu.requestAdapter();
  return { adapter };
}

async function collect(): Promise<RawData> {
  const { adapter } = await adapterInfo();
  if (!adapter) {
    throw new Error('WebGPU adapter is unavailable');
  }
  const features = [...adapter.features].sort();
  const limits = serializeLimits(adapter.limits);
  const device = await adapter.requestDevice();
  device.destroy();
  return {
    features,
    limits,
  };
}

function format(rawData: RawData): string {
  const error = formatError(rawData);
  if (error) {
    return error;
  }
  return formatHash(rawData);
}

async function run(): Promise<TestResult> {
  let rawData: RawData;
  try {
    rawData = await collect();
  } catch (error) {
    rawData = { error: errorText(error) };
  }
  return result(rawData, format);
}

function validate(rawData: RawData): ValidationResult {
  const error = validationError(rawData);
  if (error) {
    return error;
  }
  const reasons: string[] = [];
  if (!Array.isArray(rawData.features)) {
    reasons.push('WebGPU features must be an array');
  } else if (!rawData.features.every((feature) => typeof feature == 'string')) {
    reasons.push('WebGPU features must contain only strings');
  }
  if (!isRecord(rawData.limits) || Object.keys(rawData.limits).length == 0) {
    reasons.push('WebGPU limits are unavailable');
  } else {
    for (const [name, value] of Object.entries(rawData.limits)) {
      if (
        typeof value != 'number' ||
        !Number.isFinite(value) ||
        !Number.isInteger(value) ||
        value < 0
      ) {
        reasons.push(`${name} must be a nonnegative finite integer`);
      }
    }
  }
  return validation(reasons);
}

export const webGpuRenderingTest: TestDefinition = {
  id: 'webgpuRendering',
  title: 'WebGPU rendering',
  category: 'shared',
  format,
  run,
  validate,
};
